import React from 'react'
import { Radio, Select, Input, InputNumber, Switch, Typography } from 'antd'
import {
  PollQuestion as PollQuestionType,
  PollFormValue,
  getOptionValue,
  isOptionRequired,
} from '../types'

const { Text } = Typography
const { TextArea } = Input

interface PollQuestionProps {
  question: PollQuestionType
  value?: PollFormValue
  onChange?: (value: PollFormValue) => void
  /** When true, title/subtitle are rendered by Form.Item label/extra instead. */
  hideTitle?: boolean
}

export const PollQuestion: React.FC<PollQuestionProps> = ({
  question,
  value,
  onChange,
  hideTitle = false,
}) => {
  const handleChange = (newValue: PollFormValue) => {
    if (onChange) onChange(newValue)
  }

  const selectedOption = question.options.find(o => getOptionValue(o) === value)
  const isOtherSelected =
    value === '__other__' || Boolean(selectedOption?.is_other && typeof value === 'string')

  return (
    <div>
      {!hideTitle && (
        <>
          <Typography.Title level={5} style={{ marginBottom: 4 }}>
            {question.title}
            {question.is_required && <span style={{ color: '#ff4d4f' }}> *</span>}
          </Typography.Title>
          {question.subtitle && (
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
              {question.subtitle}
            </Text>
          )}
        </>
      )}

      {question.type === 'checkbox' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {question.options.map(option => {
            const optionValue = getOptionValue(option)
            const required = isOptionRequired(option)
            const selectedValues = Array.isArray(value) ? value.map(String) : []
            const checked = required || selectedValues.includes(optionValue)
            const toggle = () => {
              if (required) return
              const next = checked
                ? selectedValues.filter(v => v !== optionValue)
                : [...selectedValues, optionValue]
              handleChange(next)
            }
            return (
              <span
                key={option.id}
                role="checkbox"
                aria-checked={checked}
                aria-disabled={required}
                tabIndex={required ? -1 : 0}
                onClick={toggle}
                onKeyDown={e => {
                  if (required) return
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault()
                    toggle()
                  }
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '4px 12px',
                  borderRadius: 16,
                  border: `1px solid ${checked ? '#1677ff' : '#d9d9d9'}`,
                  background: checked ? '#1677ff' : '#fafafa',
                  color: checked ? '#fff' : 'rgba(0, 0, 0, 0.88)',
                  cursor: required ? 'not-allowed' : 'pointer',
                  opacity: required ? 0.85 : 1,
                  userSelect: 'none',
                  fontSize: 14,
                  lineHeight: '22px',
                  transition: 'all 0.2s',
                }}
              >
                {option.label}
              </span>
            )
          })}
        </div>
      )}

      {question.type === 'radio' && (
        <div>
          <Radio.Group
            value={isOtherSelected && !selectedOption ? '__other__' : value}
            onChange={e => handleChange(e.target.value)}
          >
            {question.options.map(option => (
              <Radio
                key={option.id}
                value={option.is_other ? '__other__' : getOptionValue(option)}
                style={{ display: 'block' }}
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
          {(value === '__other__' || (selectedOption?.is_other && typeof value === 'string')) && (
            <div style={{ marginTop: 8 }}>
              <TextArea
                value={typeof value === 'string' && value !== '__other__' ? value : ''}
                onChange={e => handleChange(e.target.value)}
                placeholder="Укажите свой вариант"
                style={{ width: '100%', maxWidth: 400 }}
                rows={3}
              />
            </div>
          )}
        </div>
      )}

      {question.type === 'select' && (
        <Select
          value={value as string}
          onChange={val => handleChange(val)}
          placeholder="Выберите вариант"
          style={{ width: '100%', maxWidth: 400 }}
          options={question.options.map(o => ({ label: o.label, value: getOptionValue(o) }))}
        />
      )}

      {question.type === 'text' && (
        <Input
          value={typeof value === 'string' ? value : ''}
          onChange={e => handleChange(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      )}

      {question.type === 'textarea' && (
        <TextArea
          value={typeof value === 'string' ? value : ''}
          onChange={e => handleChange(e.target.value)}
          style={{ maxWidth: 400 }}
          rows={3}
        />
      )}

      {question.type === 'number' && (
        <InputNumber
          value={typeof value === 'number' ? value : undefined}
          onChange={val => handleChange(val === null ? null : Number(val))}
          style={{ maxWidth: 200 }}
        />
      )}

      {question.type === 'switch' && (
        <Switch checked={Boolean(value)} onChange={checked => handleChange(checked)} />
      )}
    </div>
  )
}

export default PollQuestion
